import { useState, useEffect, useMemo } from "react";
import { Formik, Field, Form } from 'formik';
import { Button, Select, Option } from "@material-tailwind/react";
import { get } from "lodash";
import ApiService from "utills/ApiService";
import useWindowWidth from "utills/InitWidthDevice";
import { useTranslation } from "@refinedev/core";
import { FileUploader } from "react-drag-drop-files";
import { CameraIcon } from "@heroicons/react/outline";
import AccountWPS from "interfaces";
import { PHONE_CODE } from "utills/constants";
import { FullScreenLoading } from "components/fullScreenLoading";
import toast from "react-hot-toast";
import { Breadcrumbs } from "components/breadcrumbs";
import './edit-profile.scss';
import ReactSelect from "react-select";

const fileTypes = ["JPG", "PNG", "JPEG"];

const FormEditProfile = (props: any) => {
  const { isMobile, language, translate } = props;
  const [loading, setLoading] = useState(false);
  const [avatar, setAvatar] = useState('');
  const [phoneCode, setPhoneCode] = useState('84');
  const [profile, setProfile] = useState(null as any);
  const [events, setEvents] = useState(null as any);
  const [fetchData, setFetchData] = useState(false);
  const [token, setToken] = useState('');

  const PHONE_CODE_OPTIONS = useMemo(() => {
    return PHONE_CODE.map((pc) => {
      return {
        label: pc.label,
        value: pc.code,
      };
    });
  }, [PHONE_CODE]);

  useEffect(() => {
    const user = localStorage.getItem("USER");

    if (user) {
      const userData = JSON.parse(user);
      setToken(userData?.token);
    }
  }, []);

  useEffect(() => {
    if (token !== '') {
      const intervalFetchData = setInterval(() => {
        setFetchData(true);
        clearInterval(intervalFetchData);
      }, 100);
    }
  }, [token]);

  useEffect(() => {
    if (fetchData) {
      fetchProfile()
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchData]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const result = await ApiService.get(`account/profile`, token);

      if (result) {
        setProfile(result);

        if (result.avatar && result.avatar !== '') {
          setAvatar(result.avatar);
        }

        if (result.phone_area_code !== '') {
          setPhoneCode(result.phone_area_code);
        }

        if (result.events && result.events.length > 0) {
          fetchEvents(result.events);
        }
      }
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }

    setFetchData(false);
  }

  const fetchEvents = async (e: any) => {
    try {
      const result = await ApiService.get(`events`, token);

      if (result && result.length > 0) {
        const arr = [] as any;
        result.forEach((event: any) => {
          for (let i = 0; i < e.length; i++) {
            if (e[i] === event.id) {
              arr.push(get(event, `name_${language}`, ''));
            }
          }
        });

        setEvents([...arr]);
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
    setFetchData(false);
  }

  const handleUploadImage = async (img: any) => {
    setLoading(false);
    const data = new FormData();
    data.append('upload', img, `${img.name}`);

    try {
      const result = await ApiService.uploadImage(data);

      if (result) {
        setAvatar(result.img_url);
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    setLoading(true);
    const submitData = {
      avatar: avatar,
      last_name: values.last_name,
      first_name: values.first_name,
      phone: values.phone,
      phone_area_code: phoneCode
    };

    try {
      const result = await ApiService.put(`account/profile`, submitData, token);

      if (result) {
        toast.success(translate('update-success'))
      }
    } catch (error: any) {
      toast.error(error.message);
    }

    setLoading(false);
    setSubmitting(false);
  }

  const validateText = (value: string) => {
    let error;

    if (!value) {
      error = translate("error-field-require");
    } else {
      let str = value;
      str = str.toLowerCase();
      str = str.replace(/à|á|ạ|ả|ã|â|ầ|ấ|ậ|ẩ|ẫ|ă|ằ|ắ|ặ|ẳ|ẵ/g, 'a');
      str = str.replace(/è|é|ẹ|ẻ|ẽ|ê|ề|ế|ệ|ể|ễ/g, 'e');
      str = str.replace(/ì|í|ị|ỉ|ĩ/g, 'i');
      str = str.replace(/ò|ó|ọ|ỏ|õ|ô|ồ|ố|ộ|ổ|ỗ|ơ|ờ|ớ|ợ|ở|ỡ/g, 'o');
      str = str.replace(/ù|ú|ụ|ủ|ũ|ư|ừ|ứ|ự|ử|ữ/g, 'u');
      str = str.replace(/ỳ|ý|ỵ|ỷ|ỹ/g, 'y');
      str = str.replace(/đ/g, 'd');
      const regex = /[^a-zA-Z0-9 ]+/;

      if (regex.test(str)) {
        error = translate("error-special-character");
      }
    }

    return error;
  }

  return (
    <div className="w-full">
      <div className={`w-full bg-white rounded-[8px] ${isMobile ? "" : "mb-[86px]"}`}>
        <div className="w-full flex items-center justify-between border-b border-[#c5ccd2] h-[64px] px-[24px]">
          <div className="header-label">{translate('edit-profile')}</div>
        </div>

        <div className="w-full p-[24px]">
          <div className="bg-[#f6f5fe] border border-[#c5ccd2] rounded-full size-[88px] flex items-center justify-center mb-[24px] cursor-pointer">
            <FileUploader
              handleChange={(e: any) => handleUploadImage(e)}
              name="file"
              types={fileTypes}
              classes="text-brand-500"
            >
              {
                (avatar === '') ?
                  <CameraIcon className="h-8 w-8 cursor-pointer" />
                  :
                  <img src={avatar}
                    alt="user-avatar" className="size-[88px] rounded-full object-cover" />
              }
            </FileUploader>

          </div>

          {
            profile &&
            <Formik
              initialValues={{
                first_name: profile.first_name,
                last_name: profile.last_name,
                phone: profile.phone
              } as AccountWPS}
              onSubmit={(values, { setSubmitting }) => handleSubmit(values, { setSubmitting })}
            >
              {({
                handleSubmit,
                isSubmitting
              }) => (
                <Form onSubmit={handleSubmit}>
                  <div className={`${isMobile ? 'grid grid-cols-1 gap-[8px]' : 'grid grid-cols-2 gap-[16px]'} w-full`}>
                    <div className="flex-1">
                      <div className="flex items-center mb-[8px] gap-x-[4px]">
                        <span className="form-label">{translate('first-name')}</span>

                        <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                      </div>

                      <Field name="first_name" validate={validateText}>
                        {({
                          field,
                          meta,
                        }: any) => (
                          <div className="field-group">
                            <input {...field} type="text" className="w-full border border-[#c5ccd2] rounded-[4px]" autoComplete="off" placeholder={translate('first-name')} maxLength={50} />

                            <div className="text-[#a8b3bd] text-14 mt-[4px] text-right">{field.value.length}/50</div>

                            {meta.touched && meta.error && (
                              <div className="text-14 text-red-500 mt-[4px]">{meta.error}</div>
                            )}
                          </div>
                        )}
                      </Field>

                    </div>

                    <div className="flex-1">
                      <div className="flex items-center mb-[8px] gap-x-[4px]">
                        <span className="form-label">{translate('last-name')}</span>

                        <img src="/images/icons/icon-require.svg" alt="icon-require" className="icon-require" />
                      </div>

                      <Field name="last_name" validate={validateText}>
                        {({
                          field,
                          meta,
                        }: any) => (
                          <div className="field-group">
                            <input {...field} type="text" className="w-full border border-[#c5ccd2] rounded-[4px]" autoComplete="off" placeholder={translate('last-name')} maxLength={50} />

                            <div className="text-[#a8b3bd] text-14 mt-[4px] text-right">{field.value.length}/50</div>

                            {meta.touched && meta.error && (
                              <div className="text-14 text-red-500 mt-[4px]">{meta.error}</div>
                            )}
                          </div>
                        )}
                      </Field>

                    </div>

                    <div className="flex-1">
                      <div className="flex items-center mb-[8px] gap-x-[4px]">
                        <span className="form-label">{translate('role-name')}</span>
                      </div>

                      <div className="field-group">
                        <input type="text" className="w-full border border-[#c5ccd2] rounded-[4px] bg-[#f5f7f6] text-[#868e95] cursor-not-allowed" value={profile.role} disabled={true} />
                      </div>

                    </div>

                    <div className="flex-1">
                      <div className="flex items-center mb-[8px] gap-x-[4px]">
                        <span className="form-label">{translate('contact-phone')}</span>
                      </div>

                      {/* <div className="w-full h-[44px] flex items-center rounded-[4px] border border-[#c5ccd2] phone-group">
                        <div className="select-phone-code">
                          <Select
                            value={phoneCode}
                            onChange={(val: any) => setPhoneCode(val)}
                            placeholder=""
                            onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                            onResizeCapture={() => { }}
                          >
                            {
                              PHONE_CODE.map((item: any, index: number) => {
                                return (
                                  <Option key={`${item.value}_${index}`}
                                    value={item.code}>{item.value} | +{item.code}</Option>
                                )
                              })
                            }
                          </Select>
                        </div>

                        <Field name="phone" >
                          {({
                            field,
                            meta,
                          }: any) => (
                            <div className="input-phone-group">
                              <input {...field} type="text" className="input-phone" autoComplete="off" placeholder={translate('phone-number')} />
                            </div>
                          )}
                        </Field>
                      </div> */}

                      <div className="w-[100%] h-[42px] rounded-[4px] flex">
                        <ReactSelect
                          className={`${isMobile ? "w-[160px]" : "w-[250px]"}`}
                          styles={{
                            control: (base) => ({
                              ...base,
                              height: "42px",
                              minHeight: "42px",
                              // border: "none",
                              border: "1px solid #C5CCD2",
                              borderRadius: 0,
                              boxShadow: "none",
                              backgroundColor: "transparent",
                              borderTopLeftRadius: "4px",
                              borderBottomLeftRadius: "4px",
                            }),
                            input: (base) => ({
                              ...base,
                              "input:focus": {
                                boxShadow: "none",
                              },
                            }),
                          }}
                          value={PHONE_CODE_OPTIONS?.find((pc) => {
                            return (
                              pc.value === phoneCode
                            );
                          })}
                          options={PHONE_CODE_OPTIONS}
                          onChange={(selected) => {
                            if (selected?.value) {
                              setPhoneCode(selected?.value);
                            }
                          }}
                        />

                        <Field name="phone">
                          {({ field, meta }: any) => (
                            <input
                              {...field}
                              type="text"
                              className="border-[1px] border-l-0 focus:border-l focus:outline-none border-[#C5CCD2] w-[calc(100%-160px)]
                                                              rounded-r-[4px]"
                              autoComplete="off"
                              placeholder={translate("phone-number")}
                            />
                          )}
                        </Field>
                      </div>
                    </div>

                    <div className="flex-1">
                      <div className="flex items-center mb-[8px] gap-x-[4px]">
                        <span className="form-label">Email</span>
                      </div>

                      <div className="field-group">
                        <input type="text" className="w-full border border-[#c5ccd2] rounded-[4px] bg-[#f5f7f6] text-[#868e95] cursor-not-allowed" autoComplete="off" value={profile.email} disabled={true} />
                        <div className="text-14 text-[#a8b3bd] mt-[8px]">{translate('email-note')}</div>
                      </div>

                    </div>

                    <div className="flex-1">
                      <div className="flex items-center mb-[8px] gap-x-[4px]">
                        <span className="form-label">{translate("event")}</span>
                      </div>

                      <div className="field-group">
                        {
                          events && events.length > 0 ?
                            <div className="bg-[#f5f7f6] border border-[#c5ccd2] text-[#868e95] cursor-default px-[12px] py-[8px] rounded-[4px]">
                              {
                                events.map((item: any, index: number) => {
                                  return (
                                    <div className="event-item" key={`${item}_${index}`}>{item}</div>
                                  )
                                })
                              }
                            </div>
                            :
                            <input type="text" className="w-full border border-[#c5ccd2] rounded-[4px] bg-[#f5f7f6] text-[#868e95] cursor-not-allowed" disabled={true} />
                        }
                      </div>

                    </div>
                  </div>

                  <div className={`${isMobile ? 'w-full left-0 px-[16px]' : 'fixed w-[calc(100%-288px)] right-0 pr-[24px]'} h-[80px] fixed bg-white flex items-center justify-end bottom-0 gap-x-[12px]`}>
                    <div className="h-[48px] flex items-center justify-center px-[20px] border border-brand-500 cursor-pointer text-14 text-brand-500 rounded-[8px] font-600" onClick={() => window.history.back()}>{translate('btn-cancel')}</div>

                    <Button
                      onPointerEnterCapture="none" onPointerLeaveCapture="none" onResize={() => { }}
                      onResizeCapture={() => { }}
                      type="submit" disabled={isSubmitting} placeholder="Button Save" className="h-[48px] flex items-center justify-center px-[20px] border border-brand-500 cursor-pointer text-14 text-white bg-brand-500 rounded-[8px] font-600">{translate('btn-save')}</Button>
                  </div>
                </Form>
              )}
            </Formik>
          }

        </div>
      </div>

      {
        loading &&
        <FullScreenLoading />
      }
    </div>
  )
}

const EditProfile = () => {
  const windowWidth = useWindowWidth();
  const [isMobile, setIsMobile] = useState(false);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();

  const breadcrumbItems = [
    {
      label: translate('my-profile'),
      href: '/profile'
    },
    {
      label: translate('edit-profile'),
      href: ''
    }
  ];

  useEffect(() => {
    if (windowWidth && windowWidth < 1024) {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [windowWidth]);

  return (
    <div className="w-full">
      <Breadcrumbs type="wps" breadcrumbItems={breadcrumbItems} />

      <div className={`${isMobile ? 'p-[16px] pb-[110px]' : 'p-[16px]'} w-full`}>
        <FormEditProfile
          isMobile={isMobile}
          language={language}
          translate={translate} />
      </div>

    </div>

  )
}

export default EditProfile;